import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  GitBranch, Play, Pause, Trash2, FolderOpen,
  Brain, Search, FileText, Code, Database, MessageSquare, Image, Globe,
  ArrowRight, CheckCircle, AlertTriangle, Clock, Activity,
  BarChart3, History, Layers, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Download, Terminal, Zap, Send, Loader2, StopCircle, Share2, Mic, MicOff,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { ALL_HAVEN_TEMPLATES } from "../data/haven_templates";
import { HARVEST_ALL_TEMPLATES } from "../data/harvest_templates";
import { appendOutputPipeline } from "../data/output_pipeline";
import HavenExecutiveDashboard, { isHavenExecution } from "./HavenExecutiveDashboard";
import HarvestExecutiveDashboard, { isHarvestExecution } from "./HarvestExecutiveDashboard";
import UniversalResultsViewer, { hasOutputPipeline } from "./UniversalResultsViewer";
import PromptInput from "./PromptInput";

const API = process.env.REACT_APP_BACKEND_URL;

// 10 Agent node types
const AGENT_TYPES = [
  { type: "researcher", label: "Researcher", icon: Search, color: "#3b82f6", desc: "Web search and information gathering" },
  { type: "writer", label: "Writer", icon: FileText, color: "#8b5cf6", desc: "Content generation and copywriting" },
  { type: "coder", label: "Coder", icon: Code, color: "#10b981", desc: "Code generation and debugging" },
  { type: "analyst", label: "Analyst", icon: BarChart3, color: "#f59e0b", desc: "Data analysis and insights" },
  { type: "reviewer", label: "Reviewer", icon: CheckCircle, color: "#ef4444", desc: "Quality assurance and review" },
  { type: "translator", label: "Translator", icon: Globe, color: "#06b6d4", desc: "Multi-language translation" },
  { type: "summarizer", label: "Summarizer", icon: MessageSquare, color: "#ec4899", desc: "Text summarization" },
  { type: "image_gen", label: "Image Gen", icon: Image, color: "#f97316", desc: "AI image generation (GPT Image 1)" },
  { type: "data_proc", label: "Data Processor", icon: Database, color: "#14b8a6", desc: "ETL and data transformation" },
  { type: "video_gen", label: "Video Gen", icon: Video, color: "#dc2626", desc: "AI video generation (Sora 2)" },
  { type: "orchestrator", label: "Orchestrator", icon: GitBranch, color: "#a855f7", desc: "Sub-swarm coordination" },
];

// 6 Pre-built templates
const TEMPLATES = [
  {
    id: "research_report", name: "Research Report Generator", category: "research",
    desc: "Multi-source research with fact-checking and report compilation",
    nodes: [
      { id: "n1", type: "researcher", x: 60, y: 120, label: "Web Researcher" },
      { id: "n2", type: "researcher", x: 60, y: 280, label: "Academic Search" },
      { id: "n3", type: "analyst", x: 300, y: 200, label: "Fact Checker" },
      { id: "n4", type: "writer", x: 540, y: 200, label: "Report Writer" },
      { id: "n5", type: "reviewer", x: 740, y: 200, label: "Quality Review" },
    ],
    edges: [["n1", "n3"], ["n2", "n3"], ["n3", "n4"], ["n4", "n5"]],
  },
  {
    id: "content_pipeline", name: "Content Pipeline", category: "content",
    desc: "End-to-end content creation with SEO optimization",
    nodes: [
      { id: "n1", type: "researcher", x: 60, y: 180, label: "Topic Research" },
      { id: "n2", type: "writer", x: 280, y: 120, label: "Draft Writer" },
      { id: "n3", type: "image_gen", x: 280, y: 280, label: "Image Creator" },
      { id: "n4", type: "reviewer", x: 500, y: 180, label: "Editor" },
      { id: "n5", type: "translator", x: 700, y: 180, label: "Localizer" },
    ],
    edges: [["n1", "n2"], ["n1", "n3"], ["n2", "n4"], ["n3", "n4"], ["n4", "n5"]],
  },
  {
    id: "code_review", name: "Automated Code Review", category: "engineering",
    desc: "Multi-agent code review with security scanning",
    nodes: [
      { id: "n1", type: "coder", x: 60, y: 180, label: "Code Parser" },
      { id: "n2", type: "reviewer", x: 280, y: 100, label: "Logic Review" },
      { id: "n3", type: "reviewer", x: 280, y: 260, label: "Security Scan" },
      { id: "n4", type: "analyst", x: 500, y: 180, label: "Perf Analysis" },
      { id: "n5", type: "writer", x: 700, y: 180, label: "Report Gen" },
    ],
    edges: [["n1", "n2"], ["n1", "n3"], ["n2", "n4"], ["n3", "n4"], ["n4", "n5"]],
  },
  {
    id: "data_pipeline", name: "Data Analysis Pipeline", category: "data",
    desc: "ETL, analysis, and visualization report generation",
    nodes: [
      { id: "n1", type: "data_proc", x: 60, y: 180, label: "Data Ingestion" },
      { id: "n2", type: "data_proc", x: 280, y: 180, label: "Transform" },
      { id: "n3", type: "analyst", x: 480, y: 120, label: "Statistical Analysis" },
      { id: "n4", type: "image_gen", x: 480, y: 280, label: "Chart Gen" },
      { id: "n5", type: "writer", x: 700, y: 180, label: "Report" },
    ],
    edges: [["n1", "n2"], ["n2", "n3"], ["n2", "n4"], ["n3", "n5"], ["n4", "n5"]],
  },
  {
    id: "customer_support", name: "Customer Support Swarm", category: "support",
    desc: "Intelligent ticket routing and response generation",
    nodes: [
      { id: "n1", type: "analyst", x: 60, y: 180, label: "Ticket Classifier" },
      { id: "n2", type: "researcher", x: 280, y: 100, label: "KB Search" },
      { id: "n3", type: "summarizer", x: 280, y: 260, label: "History Summary" },
      { id: "n4", type: "writer", x: 500, y: 180, label: "Response Gen" },
      { id: "n5", type: "translator", x: 700, y: 180, label: "Localize" },
    ],
    edges: [["n1", "n2"], ["n1", "n3"], ["n2", "n4"], ["n3", "n4"], ["n4", "n5"]],
  },
  {
    id: "multi_modal", name: "Multi-Modal Content Studio", category: "creative",
    desc: "Coordinated text, image, and data content creation",
    nodes: [
      { id: "n1", type: "orchestrator", x: 60, y: 200, label: "Coordinator" },
      { id: "n2", type: "writer", x: 300, y: 80, label: "Copywriter" },
      { id: "n3", type: "image_gen", x: 300, y: 200, label: "Visual Designer" },
      { id: "n4", type: "coder", x: 300, y: 320, label: "Code Embed" },
      { id: "n5", type: "reviewer", x: 540, y: 200, label: "QA" },
      { id: "n6", type: "summarizer", x: 740, y: 200, label: "Final Package" },
    ],
    edges: [["n1", "n2"], ["n1", "n3"], ["n1", "n4"], ["n2", "n5"], ["n3", "n5"], ["n4", "n5"], ["n5", "n6"]],
  },
];

// Generate the 100-agent Vista Portfolio template programmatically
const VISTA_COMPANIES = [
  "Apptio","Avalara","Cvent","Datto","EagleView","Finastra","Gainsight","Hyland",
  "Infoblox","Jamf","KnowBe4","Logi Analytics","MeridianLink","NaviSite","Olive AI",
  "Ping Identity","QAD","RealPage","SmartBear","Thales DIS","Udacity","Veracode",
  "WP Engine","Xactly","Yellowfin","Zapproved","Apttus","Bullhorn","Civis Analytics",
  "Duck Creek","EngageSmart","Fusion Risk","Granicus","HealthEdge","iCIMS","Jordy",
  "Kofax","LogicMonitor","MiQ Digital","Numerator","OpenText","Plurilock","QuickBase",
  "Relias","Sophos","TriNet","Upserve","Vertafore","WebPT","Zywave",
  "Acquia","BlueJeans","Coupa","DataStax","ECI Software","ForgeRock","GreenPages",
  "HighJump","Idera","JFrog","Kantata","Liner","Mediaocean","Netsmart","OpsRamp",
  "Planview","QGenda","RingCentral","SecureAuth","TripActions","Unbabel","Vena",
  "Workspot","Xperi","YottaDB","ZoomInfo","Aptean","BancVue","CarePort","DigiCert",
  "Epicor","Flexera","Genesys","Harbor","iManage","JazzHR","Kyriba","LeanIX",
  "Mitratech","NovaBay","Onica","Proofpoint","Qualtrics","RFPIO",
];

function buildVistaTemplate() {
  const nodes = [];
  const edges = [];

  // Layer 0: Master Orchestrator
  nodes.push({ id: "orch_0", type: "orchestrator", x: 50, y: 400, label: "Master Orchestrator" });

  // Layer 1: 90 Company Analysts (one per portfolio company)
  const companiesPerRow = 10;
  for (let i = 0; i < 90; i++) {
    const nid = `co_${i}`;
    const row = Math.floor(i / companiesPerRow);
    const col = i % companiesPerRow;
    nodes.push({
      id: nid, type: "analyst",
      x: 200 + col * 120, y: 30 + row * 90,
      label: VISTA_COMPANIES[i] || `Company ${i + 1}`,
    });
    edges.push(["orch_0", nid]);
  }

  // Layer 2: 5 Sector Synthesizers
  const sectors = [
    { id: "sec_saas", label: "SaaS Sector Lead", range: [0, 18] },
    { id: "sec_health", label: "Healthcare IT Lead", range: [18, 36] },
    { id: "sec_fintech", label: "FinTech Lead", range: [36, 54] },
    { id: "sec_govtech", label: "GovTech Lead", range: [54, 72] },
    { id: "sec_data", label: "Data & Analytics Lead", range: [72, 90] },
  ];
  sectors.forEach((sec, si) => {
    nodes.push({ id: sec.id, type: "analyst", x: 200 + si * 250, y: 870, label: sec.label });
    for (let i = sec.range[0]; i < sec.range[1]; i++) {
      edges.push([`co_${i}`, sec.id]);
    }
  });

  // Layer 3: 3 Strategic Analysts
  const strategists = [
    { id: "strat_cost", type: "analyst", x: 300, y: 980, label: "Cost Optimization Strategist" },
    { id: "strat_rev", type: "analyst", x: 600, y: 980, label: "Revenue Acceleration Analyst" },
    { id: "strat_risk", type: "reviewer", x: 900, y: 980, label: "Risk & Compliance Reviewer" },
  ];
  strategists.forEach(s => {
    nodes.push(s);
    sectors.forEach(sec => edges.push([sec.id, s.id]));
  });

  // Layer 4: Final Package
  nodes.push({ id: "final_pkg", type: "summarizer", x: 600, y: 1090, label: "Executive Briefing" });
  strategists.forEach(s => edges.push([s.id, "final_pkg"]));

  return { nodes, edges: edges.map(([from, to]) => ([from, to])) };
}

const VISTA_TEMPLATE_DATA = buildVistaTemplate();
const VISTA_AUGMENTED = appendOutputPipeline(VISTA_TEMPLATE_DATA.nodes, VISTA_TEMPLATE_DATA.edges);

const TEMPLATES_WITH_VISTA = [
  ...TEMPLATES,
  {
    id: "vista_portfolio", name: "Vista Portfolio Intelligence (112 Agents)", category: "enterprise",
    desc: "Full PE portfolio analysis: 90 company analysts, 5 sector leads, 3 strategists, 1 executive brief + 12 Output Pipeline agents. Produces executive brief, action items, and presentation.",
    nodes: VISTA_AUGMENTED.nodes,
    edges: VISTA_AUGMENTED.edges,
  },
  ...ALL_HAVEN_TEMPLATES,
  ...HARVEST_ALL_TEMPLATES,
];

function CanvasNode({ node, selected, onSelect, onDragStart, executing, executionProgress, onViewOutput, agentResult }) {
  const agentType = AGENT_TYPES.find((a) => a.type === node.type) || AGENT_TYPES[0];
  const isOP = node.id?.startsWith("OP_");
  const Icon = agentType.icon;
  const nodeColor = isOP ? "#d97706" : agentType.color;
  const isActive = executing && executionProgress?.activeNodes?.includes(node.id);
  const isComplete = executionProgress?.completedNodes?.includes(node.id);
  const hasError = executionProgress?.errorNodes?.includes(node.id);
  const [showTip, setShowTip] = React.useState(false);
  const tipTimer = React.useRef(null);

  const roleVerb = {
    researcher: "Researches and gathers data on",
    writer: "Drafts and produces content for",
    coder: "Writes and generates code for",
    analyst: "Analyzes data and metrics for",
    reviewer: "Validates and quality-checks",
    translator: "Translates and localizes",
    summarizer: "Summarizes and distills",
    image_gen: "Generates images and visuals for",
    data_proc: "Processes and transforms data for",
    video_gen: "Produces video content for",
    orchestrator: "Coordinates and orchestrates",
  }[node.type] || "Handles";
  const briefDesc = `${roleVerb} ${node.label.replace(/([A-Z])/g, ' $1').replace(/[-_]/g, ' ').trim().toLowerCase()}`;
  const outputSnippet = agentResult?.output?.slice(0, 80);

  return (
    <motion.div
      className="absolute select-none cursor-grab active:cursor-grabbing"
      style={{ left: node.x, top: node.y }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      onMouseDown={(e) => { e.stopPropagation(); onDragStart(e, node.id); }}
      onClick={(e) => {
        e.stopPropagation();
        if (isComplete && agentResult && onViewOutput) {
          onViewOutput(agentResult);
        } else {
          onSelect(node.id);
        }
      }}
      onMouseEnter={() => { tipTimer.current = setTimeout(() => setShowTip(true), 400); }}
      onMouseLeave={() => { clearTimeout(tipTimer.current); setShowTip(false); }}
      data-testid={`swarm-node-${node.id}`}
    >
      <div className={`relative p-3 rounded-xl border-2 transition-all duration-200 min-w-[140px] ${
        selected ? "border-[var(--qn-primary)] shadow-lg shadow-[var(--qn-primary)]/20" :
        hasError ? "border-red-500 shadow-lg shadow-red-500/20" :
        isActive ? "border-amber-500 shadow-lg shadow-amber-500/20" :
        isComplete ? "border-emerald-500 hover:border-emerald-400 cursor-pointer" :
        "border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40"
      } bg-[var(--qn-card)]`}>
        {isOP && !isActive && !isComplete && (
          <div className="absolute inset-0 rounded-xl bg-amber-500/[0.04] border border-amber-500/20 pointer-events-none" />
        )}
        {isActive && (
          <motion.div
            className="absolute -inset-1 rounded-xl border-2 border-amber-500/50"
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: nodeColor + "20" }}>
            <Icon className="w-4 h-4" style={{ color: nodeColor }} />
          </div>
          <span className="text-xs font-medium text-[var(--qn-foreground)] truncate">{node.label}</span>
        </div>
        <div className="text-[10px] text-[var(--qn-foreground-muted)]">{agentType.label}</div>
        {/* Output preview on completed nodes */}
        {isComplete && outputSnippet && (
          <div className="mt-1.5 text-[9px] text-emerald-400/70 line-clamp-1 italic">
            {outputSnippet}...
          </div>
        )}
        {isComplete && !hasError && <CheckCircle className="absolute -top-1.5 -right-1.5 w-4 h-4 text-emerald-500 fill-emerald-500/20" />}
        {hasError && <AlertTriangle className="absolute -top-1.5 -right-1.5 w-4 h-4 text-red-500" />}
        {isActive && <Loader2 className="absolute -top-1.5 -right-1.5 w-4 h-4 text-amber-500 animate-spin" />}
        {isComplete && agentResult && (
          <div className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center cursor-pointer" title="Click to view output">
            <ChevronUp className="w-3 h-3 text-white" />
          </div>
        )}
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[var(--qn-primary)] border-2 border-[var(--qn-card)] opacity-0 hover:opacity-100 transition-opacity" />
        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[var(--qn-primary)] border-2 border-[var(--qn-card)] opacity-0 hover:opacity-100 transition-opacity" />

        {/* Hover Tooltip */}
        <AnimatePresence>
          {showTip && (
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-56 pointer-events-none"
              data-testid={`tooltip-${node.id}`}
            >
              <div className="p-2.5 rounded-lg bg-[#0c1220] border border-[var(--qn-border)] shadow-xl shadow-black/40">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ background: nodeColor + "25" }}>
                    <Icon className="w-3 h-3" style={{ color: nodeColor }} />
                  </div>
                  <span className="text-[11px] font-semibold text-[var(--qn-foreground)] leading-tight">{node.label}</span>
                </div>
                <p className="text-[10px] leading-relaxed text-[var(--qn-foreground-muted)]">{briefDesc}</p>
                {isComplete && <p className="text-[9px] text-emerald-400 mt-1">Click to view full output</p>}
                <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-white/[0.06]">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: nodeColor + "15", color: nodeColor }}>{isOP ? "Output Pipeline" : agentType.label}</span>
                  <span className="text-[9px] text-[var(--qn-foreground-subtle)]">ID: {node.id}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function CanvasEdge({ from, to, executing, completedEdges }) {
  if (!from || !to) return null;
  const x1 = from.x + 140;
  const y1 = from.y + 28;
  const x2 = to.x;
  const y2 = to.y + 28;
  const mx = (x1 + x2) / 2;
  const isComplete = completedEdges?.includes(`${from.id}-${to.id}`);

  return (
    <g>
      <path
        d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
        fill="none"
        stroke={isComplete ? "#10b981" : "var(--qn-border)"}
        strokeWidth={isComplete ? 2.5 : 1.5}
        strokeDasharray={executing && !isComplete ? "6 4" : "none"}
        className="transition-all duration-300"
      />
      {executing && !isComplete && (
        <circle r="3" fill="var(--qn-primary)">
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            path={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
          />
        </circle>
      )}
      <polygon
        points={`${x2},${y2} ${x2-8},${y2-4} ${x2-8},${y2+4}`}
        fill={isComplete ? "#10b981" : "var(--qn-border)"}
        className="transition-all duration-300"
      />
    </g>
  );
}

// Node output viewer
function NodeOutputCard({ result, expanded, onToggle }) {
  const agentType = AGENT_TYPES.find((a) => a.type === result.type) || AGENT_TYPES[0];
  const Icon = agentType.icon;
  const hasMedia = result.media_type && result.media_url;

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-200 ${
        result.error ? "border-red-500/40 bg-red-500/5" : "border-[var(--qn-border)] bg-[var(--qn-card)]"
      }`}
      data-testid={`output-card-${result.node_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-[var(--qn-surface)]/30 transition-colors"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: agentType.color + "20" }}>
          <Icon className="w-4 h-4" style={{ color: agentType.color }} />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--qn-foreground)]">
            {result.label}
            {hasMedia && (
              <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]">
                {result.media_type === "video" ? "VIDEO" : "IMAGE"}
              </span>
            )}
            {result.model_display_name && (
              <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400" data-testid={`model-tag-${result.node_id}`}>
                {result.model_display_name}
                {result.routing_confidence && result.routing_confidence < 1 && (
                  <span className="ml-1 opacity-60">{Math.round(result.routing_confidence * 100)}%</span>
                )}
              </span>
            )}
          </div>
          <div className="text-[10px] text-[var(--qn-foreground-muted)]">
            {result.tokens} tokens &middot; ${result.cost?.toFixed(4)} &middot; {result.latency_ms}ms
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result.error ? (
            <Badge variant="destructive" className="text-[10px]">Error</Badge>
          ) : (
            <Badge className="text-[10px] bg-emerald-600">Complete</Badge>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--qn-foreground-muted)]" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 border-t border-[var(--qn-border)]/30">
              {/* Model routing info */}
              {result.model_display_name && (
                <div className="mt-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-[10px]" data-testid={`routing-info-${result.node_id}`}>
                  <div className="flex items-center gap-3 text-amber-400">
                    <span className="font-semibold">Model: {result.model_display_name}</span>
                    <span className="text-[var(--qn-foreground-muted)]">|</span>
                    <span>Provider: {result.model_provider || "—"}</span>
                    {result.routing_confidence && <><span className="text-[var(--qn-foreground-muted)]">|</span><span>Confidence: {Math.round(result.routing_confidence * 100)}%</span></>}
                  </div>
                  {result.routing_reasoning && (
                    <div className="mt-1 text-[var(--qn-foreground-muted)]">{result.routing_reasoning}</div>
                  )}
                </div>
              )}
              {/* Render media inline */}
              {hasMedia && result.media_type === "image" && (
                <div className="mt-3 rounded-lg overflow-hidden border border-[var(--qn-border)]" data-testid={`media-image-${result.node_id}`}>
                  <img
                    src={`${API}${result.media_url}`}
                    alt={result.output || "Generated image"}
                    className="w-full max-h-[400px] object-contain bg-black/20"
                    loading="lazy"
                  />
                  <a
                    href={`${API}${result.media_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-[var(--qn-primary)] hover:underline"
                  >
                    <Download className="w-3 h-3" /> Download full resolution
                  </a>
                </div>
              )}
              {hasMedia && result.media_type === "video" && (
                <div className="mt-3 rounded-lg overflow-hidden border border-[var(--qn-border)]" data-testid={`media-video-${result.node_id}`}>
                  <video
                    src={`${API}${result.media_url}`}
                    controls
                    className="w-full max-h-[400px] bg-black"
                    preload="metadata"
                  />
                  <a
                    href={`${API}${result.media_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-[var(--qn-primary)] hover:underline"
                  >
                    <Download className="w-3 h-3" /> Download video
                  </a>
                </div>
              )}
              <div className="mt-3 text-xs text-[var(--qn-foreground)] leading-relaxed max-h-[300px] overflow-y-auto prose prose-invert prose-xs">
                <ReactMarkdown>{result.output}</ReactMarkdown>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Format cost as $X,XXX.XX (clear dollar format)
const fmtCost = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SwarmBuilder() {
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState("canvas");
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [swarmName, setSwarmName] = useState("Untitled Swarm");
  const [executing, setExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [agentResults, setAgentResults] = useState([]);
  const [expandedOutputs, setExpandedOutputs] = useState({});
  const [executionSummary, setExecutionSummary] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetailLoading, setExecutionDetailLoading] = useState(false);
  const [executionDetail, setExecutionDetail] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const [detailExpandedOutputs, setDetailExpandedOutputs] = useState({});
  const [viewingOutput, setViewingOutput] = useState(null); // Agent output modal
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef({ dragging: false, nodeId: null, offsetX: 0, offsetY: 0 });
  const abortRef = useRef(null);

  // Voice Copilot: listen for execution commands from the SwarmOrchestrator
  useEffect(() => {
    const handleVoiceExecute = (e) => {
      const { prompt: voicePrompt, workflow } = e.detail || {};
      const userPrompt = voicePrompt || workflow || "";
      if (!userPrompt) return;
      console.log("[SwarmBuilder] Voice execution triggered:", userPrompt);
      // Set the prompt and trigger execution on next tick
      setPrompt(userPrompt);
      // Use a short delay so the prompt state updates before startExecution reads it
      setTimeout(() => {
        const executeBtn = document.querySelector('[data-testid="swarm-execute-btn"]');
        if (executeBtn && !executeBtn.disabled) {
          executeBtn.click();
        } else {
          toast.error("Load a template first, then try voice execution.");
        }
      }, 300);
    };
    document.addEventListener("qnneal:executeWorkflow", handleVoiceExecute);
    return () => document.removeEventListener("qnneal:executeWorkflow", handleVoiceExecute);
  }, []);

  const shareTemplate = async () => {
    if (nodes.length === 0) { toast.error("Add nodes before sharing"); return; }
    try {
      const res = await fetch(`${API}/api/swarms/templates/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: swarmName,
          description: `${nodes.length} agents, ${edges.length} connections`,
          nodes: nodes.map(n => ({ id: n.id, type: n.type, label: n.label, x: n.x, y: n.y })),
          edges: edges.map(e => ({ from: e.from, to: e.to })),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        await navigator.clipboard.writeText(`${window.location.origin}${data.share_url}`);
        toast.success("Share link copied to clipboard!");
      } else {
        toast.error("Failed to create share link");
      }
    } catch (err) {
      toast.error("Share failed");
    }
  };

  const loadTemplate = (template) => {
    const tmplNodes = template.nodes.map((n) => ({ ...n }));
    const tmplEdges = template.edges.map((e) =>
      Array.isArray(e) ? { from: e[0], to: e[1] } : { from: e.from, to: e.to }
    );
    setNodes(tmplNodes);
    setEdges(tmplEdges);
    setSwarmName(template.name);
    setTemplateDialogOpen(false);
    setSelectedNode(null);
    setAgentResults([]);
    setExecutionSummary(null);
    setExecutionProgress(null);

    // Auto-fit: zoom to fill canvas after a brief render tick
    requestAnimationFrame(() => {
      if (tmplNodes.length > 0 && canvasRef.current) {
        const maxX = Math.max(...tmplNodes.map(n => n.x || 0));
        const maxY = Math.max(...tmplNodes.map(n => n.y || 0));
        const canvasW = canvasRef.current.clientWidth || 800;
        const canvasH = canvasRef.current.clientHeight || 500;
        const fitZoom = Math.min(canvasW / (maxX + 220), canvasH / (maxY + 150), 1.5);
        setZoom(+Math.max(0.25, Math.min(fitZoom, 1.2)).toFixed(2));
        setPan({ x: 0, y: 0 });
      }
    });

    toast.success(`Loaded template: ${template.name}`);
  };

  const addNode = (type) => {
    const agentType = AGENT_TYPES.find((a) => a.type === type);
    const id = `n_${Date.now().toString(36)}`;
    setNodes((prev) => [...prev, {
      id, type,
      x: 100 + Math.random() * 300,
      y: 100 + Math.random() * 200,
      label: agentType.label,
    }]);
  };

  const deleteNode = (nodeId) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setEdges((prev) => prev.filter((e) => e.from !== nodeId && e.to !== nodeId));
    if (selectedNode === nodeId) setSelectedNode(null);
  };

  const onDragStart = useCallback((e, nodeId) => {
    const node = document.querySelector(`[data-testid="swarm-node-${nodeId}"]`);
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragRef.current = { dragging: true, nodeId, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
  }, []);

  const onDrag = useCallback((e) => {
    if (!dragRef.current.dragging || !canvasRef.current) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - canvasRect.left - dragRef.current.offsetX) / zoom - pan.x / zoom;
    const y = (e.clientY - canvasRect.top - dragRef.current.offsetY) / zoom - pan.y / zoom;
    setNodes((prev) => prev.map((n) => n.id === dragRef.current.nodeId ? { ...n, x: Math.max(0, x), y: Math.max(0, y) } : n));
  }, [zoom, pan]);

  const onDragEnd = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Fetch execution history
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/dashboard/swarm-executions`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setExecutionHistory(data.executions || []);
      }
    } catch (err) {
      console.error("Failed to load history:", err);
    }
    setHistoryLoading(false);
  }, []);

  // Fetch analytics
  const loadAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/swarm-analytics`, { credentials: "include" });
      if (res.ok) {
        setAnalyticsData(await res.json());
      }
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
  }, []);

  // Load execution detail (full agent outputs)
  const loadExecutionDetail = useCallback(async (executionId) => {
    setExecutionDetailLoading(true);
    setSelectedExecution(executionId);
    setDetailExpandedOutputs({});
    try {
      const res = await fetch(`${API}/api/swarms/executions/${executionId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setExecutionDetail(data);
        // Auto-expand all outputs
        const expanded = {};
        if (data.agent_results) {
          Object.keys(data.agent_results).forEach(k => { expanded[k] = true; });
        }
        setDetailExpandedOutputs(expanded);
      }
    } catch (err) {
      console.error("Failed to load execution detail:", err);
      toast.error("Failed to load execution details");
    }
    setExecutionDetailLoading(false);
  }, []);

  useEffect(() => {
    if (activeSubTab === "history") loadHistory();
    if (activeSubTab === "analytics") loadAnalytics();
  }, [activeSubTab, loadHistory, loadAnalytics]);

  // Voice-to-prompt via MediaRecorder + server-side Whisper
  const toggleVoice = useCallback(async () => {
    // If already listening, stop recording
    if (isListening && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release the mic
        stream.getTracks().forEach((t) => t.stop());
        setIsListening(false);

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) {
          toast.error("Recording too short. Please speak longer.");
          return;
        }

        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append("audio", blob, "recording.webm");
          const res = await fetch(`${API}/api/swarms/transcribe`, {
            method: "POST",
            credentials: "include",
            body: formData,
          });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              setPrompt((prev) => (prev ? prev + " " + data.text : data.text));
              toast.success("Transcription complete");
            } else {
              toast.error("No speech detected. Try again.");
            }
          } else {
            const err = await res.json().catch(() => ({}));
            toast.error(err.detail || "Transcription failed");
          }
        } catch (err) {
          toast.error("Transcription request failed: " + (err.message || "Network error"));
        }
        setIsTranscribing(false);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250); // collect chunks every 250ms
      setIsListening(true);
      toast.success("Listening... tap mic to stop");
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Microphone access denied. Please allow mic permissions.");
      } else {
        toast.error("Could not access microphone: " + (err.message || "Unknown error"));
      }
    }
  }, [isListening]);

  // Poll execution status when SSE stream disconnects
  const pollExecutionUntilDone = async (executionId) => {
    const maxPollTime = 900000; // 15 minutes
    const pollInterval = 3000; // 3 seconds
    const startTime = Date.now();
    const seenNodes = new Set();

    // Track nodes already received via SSE
    setAgentResults(prev => {
      prev.forEach(r => seenNodes.add(r.node_id));
      return prev;
    });

    setExecutionProgress(prev => ({
      ...prev,
      log: [...(prev?.log || []), { time: new Date().toLocaleTimeString(), msg: "Stream reconnecting — polling for updates...", type: "info" }],
    }));

    while (Date.now() - startTime < maxPollTime) {
      try {
        const res = await fetch(`${API}/api/swarms/executions/${executionId}`, { credentials: "include" });
        if (!res.ok) break;
        const data = await res.json();
        const results = data.agent_results || {};

        // Only emit events for NEW nodes not yet seen
        for (const [nodeId, r] of Object.entries(results)) {
          if (!seenNodes.has(nodeId)) {
            seenNodes.add(nodeId);
            handleSSEEvent("node_completed", {
              node_id: nodeId, label: r.label, type: r.type,
              output: r.output, tokens: r.tokens, cost: r.cost,
              latency_ms: r.latency_ms, error: r.error,
              media_type: r.media_type, media_url: r.media_url,
            });
          }
        }

        const status = data.status;
        if (["completed", "completed_with_errors", "failed"].includes(status)) {
          handleSSEEvent("execution_completed", {
            execution_id: executionId, status,
            duration_ms: data.duration_ms, total_tokens: data.total_tokens,
            total_cost: data.total_cost,
          });
          return;
        }
      } catch (e) {
        // Network error during poll — continue trying
      }
      await new Promise(r => setTimeout(r, pollInterval));
    }
  };

  // Real execution via SSE with auto-reconnect fallback
  const startExecution = async () => {
    if (nodes.length === 0) { toast.error("Add nodes to the canvas first"); return; }

    setExecuting(true);
    setAgentResults([]);
    setExecutionSummary(null);
    setExpandedOutputs({});
    setExecutionProgress({ activeNodes: [], completedNodes: [], errorNodes: [], completedEdges: [], log: [] });

    let executionId = null;

    try {
      // Step 1: Create execution
      const createRes = await fetch(`${API}/api/swarms/execute-live`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          swarm_name: swarmName,
          nodes: nodes.map(n => ({
            id: n.id, type: n.type, label: n.label, x: n.x, y: n.y,
            // Preserve Output Pipeline metadata
            ...(n.is_output_pipeline ? { is_output_pipeline: true, preferred_model: n.preferred_model, op_role: n.op_role } : {}),
          })),
          edges: edges.map(e => ({ from: e.from, to: e.to })),
          prompt,
        }),
      });

      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({ detail: "Execution failed" }));
        toast.error(err.detail || "Failed to start execution");
        setExecuting(false);
        return;
      }

      const createData = await createRes.json();
      executionId = createData.execution_id;

      setExecutionProgress(prev => ({
        ...prev,
        log: [...(prev?.log || []), { time: new Date().toLocaleTimeString(), msg: `Execution ${executionId} created — ${nodes.length} agents`, type: "info" }],
      }));

      // Step 2: Connect to SSE stream with retry
      let streamCompleted = false;
      const maxRetries = 3;

      for (let attempt = 0; attempt < maxRetries && !streamCompleted; attempt++) {
        try {
          if (attempt > 0) {
            setExecutionProgress(prev => ({
              ...prev,
              log: [...(prev?.log || []), { time: new Date().toLocaleTimeString(), msg: `Reconnecting stream (attempt ${attempt + 1})...`, type: "info" }],
            }));
            await new Promise(r => setTimeout(r, 2000));
          }

          const streamRes = await fetch(`${API}/api/swarms/executions/${executionId}/stream`, {
            credentials: "include",
          });

          if (!streamRes.ok || !streamRes.body) {
            continue; // retry
          }

          const reader = streamRes.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            let currentEvent = "";
            for (const line of lines) {
              if (line.startsWith("event: ")) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith("data: ") && currentEvent) {
                try {
                  const data = JSON.parse(line.slice(6));
                  handleSSEEvent(currentEvent, data);
                  if (currentEvent === "execution_completed" || currentEvent === "stream_end") {
                    streamCompleted = true;
                  }
                } catch (e) {
                  // skip malformed
                }
                currentEvent = "";
              }
            }
          }

          // If stream ended normally with completion event
          if (streamCompleted) break;
        } catch (streamErr) {
          console.warn(`SSE stream attempt ${attempt + 1} failed:`, streamErr.message);
        }
      }

      // If SSE stream never completed, fall back to polling
      if (!streamCompleted && executionId) {
        await pollExecutionUntilDone(executionId);
      }
    } catch (err) {
      console.error("Execution error:", err);
      // If we have an executionId, the backend is still running — poll for it
      if (executionId) {
        toast.info("Stream disconnected — tracking execution via polling...");
        await pollExecutionUntilDone(executionId);
      } else {
        toast.error("Execution failed: " + (err.message || "Unknown error"));
      }
    }

    setExecuting(false);
  };

  const handleSSEEvent = (event, data) => {
    switch (event) {
      case "execution_started":
        setExecutionProgress(prev => ({
          ...prev,
          log: [...(prev?.log || []), { time: new Date().toLocaleTimeString(), msg: `Execution started (${data.total_nodes} agents)`, type: "info" }],
        }));
        break;

      case "layer_started":
        setExecutionProgress(prev => ({
          ...prev,
          activeNodes: data.node_ids,
          log: [...(prev?.log || []), {
            time: new Date().toLocaleTimeString(),
            msg: data.batch_plan?.method === "quantum_annealing_sa"
              ? `Layer ${data.layer}: ${data.node_labels.length} agents | Quantum-optimized: ${data.batch_plan.num_batches} batches of ${data.batch_plan.batch_size} (~${data.batch_plan.estimated_time_s}s)`
              : `Layer ${data.layer}: Processing ${data.node_labels.length <= 6 ? data.node_labels.join(", ") : data.node_labels.length + " agents in parallel"}`,
            type: "info",
          }],
        }));
        break;

      case "batch_started":
        setExecutionProgress(prev => ({
          ...prev,
          log: [...(prev?.log || []), {
            time: new Date().toLocaleTimeString(),
            msg: `  Batch ${data.batch}/${data.total_batches}: ${data.batch_size} agents (${data.method})`,
            type: "info",
          }],
        }));
        break;

      case "node_completed": {
        const { node_id, label, output, tokens, cost, latency_ms, error, media_type, media_url } = data;

        // Deduplicate: skip if this node was already completed (SSE reconnect replay)
        setAgentResults(prev => {
          if (prev.some(r => r.node_id === node_id)) return prev;
          return [...prev, data];
        });

        // Auto-expand the latest completed node
        setExpandedOutputs(prev => ({ ...prev, [node_id]: true }));

        setExecutionProgress(prev => {
          // Skip duplicate completions
          if (prev?.completedNodes?.includes(node_id)) return prev;
          const completedNodes = [...(prev?.completedNodes || []), node_id];
          const errorNodes = error ? [...(prev?.errorNodes || []), node_id] : (prev?.errorNodes || []);
          const activeNodes = (prev?.activeNodes || []).filter(id => id !== node_id);

          // Update completed edges
          const newEdges = edges
            .filter(e => completedNodes.includes(e.from) && completedNodes.includes(e.to))
            .map(e => `${e.from}-${e.to}`);

          return {
            ...prev,
            activeNodes,
            completedNodes,
            errorNodes,
            completedEdges: newEdges,
            log: [...(prev?.log || []), {
              time: new Date().toLocaleTimeString(),
              msg: `${label}: ${error ? "Error" : "Complete"} (${tokens} tokens, $${cost?.toFixed(4)}, ${latency_ms}ms)`,
              type: error ? "error" : "success",
            }],
          };
        });
        break;
      }

      case "execution_completed":
        setExecutionSummary(data);
        setExecutionProgress(prev => ({
          ...prev,
          activeNodes: [],
          log: [...(prev?.log || []), {
            time: new Date().toLocaleTimeString(),
            msg: `Execution ${data.status}: ${data.total_tokens} tokens, ${fmtCost(data.total_cost)}, ${(data.duration_ms / 1000).toFixed(1)}s`,
            type: data.status === "completed" ? "success" : "warning",
          }],
        }));
        toast.success(`Swarm execution complete! ${data.total_tokens} tokens used.`);
        break;

      case "error":
        setExecutionProgress(prev => ({
          ...prev,
          log: [...(prev?.log || []), {
            time: new Date().toLocaleTimeString(),
            msg: `Error: ${data.message}`,
            type: "error",
          }],
        }));
        toast.error(data.message);
        break;

      default:
        break;
    }
  };

  const toggleOutput = (nodeId) => {
    setExpandedOutputs(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const stopExecution = () => {
    setExecuting(false);
    toast.info("Execution stopped");
  };

  // Build a map of agent results by node_id for canvas node access
  const agentResultsMap = useMemo(() => {
    const map = {};
    agentResults.forEach(r => { map[r.node_id] = r; });
    return map;
  }, [agentResults]);

  const completedCount = executionProgress?.completedNodes?.length || 0;
  const totalNodes = nodes.length;

  const nodeMap = useMemo(() => {
    const map = {};
    nodes.forEach((n) => { map[n.id] = n; });
    return map;
  }, [nodes]);

  return (
    <div className="space-y-6" data-testid="swarm-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">Agent Swarm Orchestration</h1>
          <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">Build, execute, and monitor multi-agent workflows</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setTemplateDialogOpen(true)} data-testid="swarm-templates-btn">
            <FolderOpen className="w-4 h-4 mr-2" /> Templates
          </Button>
          <Button variant="outline" size="sm" onClick={shareTemplate} disabled={nodes.length === 0} data-testid="swarm-share-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button
            size="sm"
            onClick={executing ? stopExecution : startExecution}
            disabled={nodes.length === 0 && !executing}
            data-testid="swarm-execute-btn"
            className={`relative overflow-hidden ${executing ? "bg-amber-600 hover:bg-amber-700 min-w-[180px]" : "bg-emerald-600 hover:bg-emerald-700"}`}
          >
            {executing ? (
              <>
                {/* Progress bar fill inside button */}
                {nodes.length > 0 && (
                  <motion.div
                    className="absolute inset-0 bg-amber-500/25"
                    initial={{ width: "0%" }}
                    animate={{ width: `${Math.round(((executionProgress?.completedNodes?.length || 0) / nodes.length) * 100)}%` }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Executing</span>
                  <span className="font-mono text-xs opacity-80">
                    {executionProgress?.completedNodes?.length || 0}/{nodes.length}
                  </span>
                </span>
              </>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Execute</>
            )}
          </Button>
        </div>
      </div>

      {/* Execution Progress Bar */}
      <AnimatePresence>
        {executing && nodes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3"
            data-testid="execution-progress-bar"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                <span className="text-sm font-medium text-amber-400">
                  Swarm Executing — {executionProgress?.completedNodes?.length || 0} of {nodes.length} agents complete
                </span>
              </div>
              <div className="flex items-center gap-3">
                {executionProgress?.activeNodes?.length > 0 && (
                  <span className="text-xs text-[var(--qn-foreground-muted)]">
                    {executionProgress.activeNodes.length} agent{executionProgress.activeNodes.length > 1 ? "s" : ""} running
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stopExecution}
                  className="h-7 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  data-testid="stop-execution-btn"
                >
                  <StopCircle className="w-3.5 h-3.5 mr-1.5" /> Stop
                </Button>
              </div>
            </div>
            <div className="w-full h-2 rounded-full bg-[var(--qn-surface)] overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-500"
                initial={{ width: "0%" }}
                animate={{ width: `${Math.round(((executionProgress?.completedNodes?.length || 0) / nodes.length) * 100)}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          <TabsTrigger value="canvas" data-testid="swarm-tab-canvas"><GitBranch className="w-4 h-4 mr-2" />Canvas</TabsTrigger>
          <TabsTrigger value="history" data-testid="swarm-tab-history"><History className="w-4 h-4 mr-2" />History</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="swarm-tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
        </TabsList>

        {/* Canvas Tab */}
        <TabsContent value="canvas">
          {/* Agent Palette — horizontal strip above canvas */}
          <div className="mb-3 flex items-center gap-1.5 p-1.5 rounded-xl bg-[var(--qn-card)] border border-[var(--qn-border)] overflow-x-auto" data-testid="agent-palette">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] px-2 shrink-0">Agents</span>
            {AGENT_TYPES.map((agent) => {
              const Icon = agent.icon;
              return (
                <button
                  key={agent.type}
                  onClick={() => addNode(agent.type)}
                  title={`Add ${agent.label}: ${agent.desc}`}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-[var(--qn-surface)] transition-colors shrink-0 group"
                  data-testid={`add-agent-${agent.type}`}
                >
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: agent.color + "20" }}>
                    <Icon className="w-3 h-3" style={{ color: agent.color }} />
                  </div>
                  <span className="text-[11px] font-medium text-[var(--qn-foreground-muted)] group-hover:text-[var(--qn-foreground)] transition-colors">{agent.label}</span>
                </button>
              );
            })}
          </div>

          {/* Canvas + Prompt + Output */}
          <div className="space-y-3">
              {/* Canvas Toolbar */}
              <div className="flex items-center gap-3">
                <Input
                  value={swarmName}
                  onChange={(e) => setSwarmName(e.target.value)}
                  className="bg-[var(--qn-surface)] border-[var(--qn-border)] max-w-[240px] text-sm font-medium"
                  data-testid="swarm-name-input"
                />
                {selectedNode && (
                  <Button variant="outline" size="sm" onClick={() => deleteNode(selectedNode)} className="text-red-400 border-red-400/20">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Node
                  </Button>
                )}
                <div className="ml-auto text-xs text-[var(--qn-foreground-muted)]">
                  {nodes.length} nodes, {edges.length} connections
                </div>
              </div>

              {/* Prompt Input — ChatGPT-style */}
              <PromptInput
                value={prompt}
                onChange={setPrompt}
                onSubmit={startExecution}
                placeholder={isListening ? "Listening... speak your prompt" : isTranscribing ? "Transcribing..." : "Ask anything — describe your swarm task"}
                disabled={executing || isTranscribing}
                loading={executing}
                maxLength={10000}
                showMic={true}
                isListening={isListening}
                onMicToggle={toggleVoice}
                submitLabel="Execute Swarm"
                data-testid="swarm-prompt-input"
              />
              {(isListening || isTranscribing) && (
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className={`w-2 h-2 rounded-full animate-pulse ${isTranscribing ? "bg-amber-500" : "bg-red-500"}`} />
                  <span className={`text-xs ${isTranscribing ? "text-amber-400" : "text-red-400"}`}>
                    {isTranscribing ? "Transcribing with Whisper..." : "Recording... tap mic to stop"}
                  </span>
                </div>
              )}

              {/* Execution Progress Bar */}
              {executing && executionProgress && (
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] p-3" data-testid="execution-progress-bar">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                      <span className="text-sm font-semibold text-[var(--qn-foreground)]">
                        Executing: {completedCount}/{totalNodes} agents
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--qn-foreground-muted)]">
                      {(executionProgress.errorNodes?.length || 0) > 0 && (
                        <span className="text-red-400">{executionProgress.errorNodes.length} errors</span>
                      )}
                      <span>{Math.round((completedCount / Math.max(totalNodes, 1)) * 100)}%</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-[var(--qn-surface)] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${(completedCount / Math.max(totalNodes, 1)) * 100}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                  {executionProgress.activeNodes?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {executionProgress.activeNodes.slice(0, 8).map(nid => {
                        const n = nodeMap[nid];
                        return n ? (
                          <Badge key={nid} variant="outline" className="text-[9px] text-amber-400 border-amber-500/30 animate-pulse">
                            {n.label}
                          </Badge>
                        ) : null;
                      })}
                      {executionProgress.activeNodes.length > 8 && (
                        <Badge variant="outline" className="text-[9px] text-[var(--qn-foreground-muted)]">
                          +{executionProgress.activeNodes.length - 8} more
                        </Badge>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Completed execution summary bar */}
              {!executing && agentResults.length > 0 && (
                <Card className="bg-emerald-500/5 border-emerald-500/30 p-3" data-testid="execution-complete-bar">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <div>
                        <span className="text-sm font-semibold text-emerald-400">
                          Execution Complete — {agentResults.length} agents finished
                        </span>
                        {executionSummary && (
                          <span className="text-xs text-[var(--qn-foreground-muted)] ml-2">
                            {executionSummary.total_tokens?.toLocaleString()} tokens &middot; {fmtCost(executionSummary.total_cost)} &middot; {(executionSummary.duration_ms / 1000).toFixed(0)}s
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowResultsPanel(p => !p)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="view-results-btn"
                    >
                      <FileText className="w-4 h-4 mr-1.5" />
                      {showResultsPanel ? "Hide Results" : "View All Results"}
                    </Button>
                  </div>
                </Card>
              )}

              {/* Canvas */}
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] relative">
                {/* Zoom Controls */}
                <div className="absolute top-3 right-3 z-30 flex items-center gap-1 bg-[var(--qn-card)]/90 backdrop-blur-sm rounded-lg border border-[var(--qn-border)] p-1 shadow-lg" data-testid="zoom-controls">
                  <button
                    onClick={() => setZoom(z => Math.min(2, +(z + 0.15).toFixed(2)))}
                    className="w-7 h-7 rounded flex items-center justify-center text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface)] transition-colors text-sm font-bold"
                    data-testid="zoom-in-btn"
                    title="Zoom in"
                  >+</button>
                  <button
                    onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                    className="px-2 h-7 rounded flex items-center justify-center text-[10px] font-mono text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface)] transition-colors min-w-[40px]"
                    data-testid="zoom-reset-btn"
                    title="Reset zoom (click to reset)"
                  >{Math.round(zoom * 100)}%</button>
                  <button
                    onClick={() => setZoom(z => Math.max(0.2, +(z - 0.15).toFixed(2)))}
                    className="w-7 h-7 rounded flex items-center justify-center text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface)] transition-colors text-sm font-bold"
                    data-testid="zoom-out-btn"
                    title="Zoom out"
                  >-</button>
                  <div className="w-px h-5 bg-[var(--qn-border)] mx-0.5" />
                  <button
                    onClick={() => {
                      if (nodes.length === 0) return;
                      const maxX = Math.max(...nodes.map(n => n.x));
                      const maxY = Math.max(...nodes.map(n => n.y));
                      const canvasW = canvasRef.current?.clientWidth || 800;
                      const canvasH = canvasRef.current?.clientHeight || 420;
                      const fitZoom = Math.min(canvasW / (maxX + 200), canvasH / (maxY + 120), 1);
                      setZoom(+Math.max(0.2, fitZoom).toFixed(2));
                      setPan({ x: 0, y: 0 });
                    }}
                    className="w-7 h-7 rounded flex items-center justify-center text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface)] transition-colors"
                    data-testid="zoom-fit-btn"
                    title="Fit all agents"
                  ><Layers className="w-3.5 h-3.5" /></button>
                </div>

                <CardContent className="p-0">
                  <div
                    ref={canvasRef}
                    className="relative bg-[var(--qn-surface)]/30 overflow-auto cursor-grab active:cursor-grabbing w-full"
                    style={{
                      minHeight: "500px",
                      height: nodes.length > 0
                        ? `${Math.max(500, (Math.max(...nodes.map(n => n.y)) + 150) * zoom)}px`
                        : "500px",
                      backgroundImage: "radial-gradient(circle, var(--qn-border) 1px, transparent 1px)",
                      backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
                      backgroundPosition: `${pan.x}px ${pan.y}px`,
                    }}
                    onWheel={(e) => {
                      if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        setZoom(z => Math.min(2, Math.max(0.2, +(z + (e.deltaY > 0 ? -0.08 : 0.08)).toFixed(2))));
                      }
                    }}
                    onMouseDown={(e) => {
                      if (e.target === canvasRef.current || e.target.tagName === "svg") {
                        isPanning.current = true;
                        panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
                      }
                    }}
                    onMouseMove={(e) => {
                      if (isPanning.current) {
                        setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
                      }
                      onDrag(e);
                    }}
                    onMouseUp={(e) => { isPanning.current = false; onDragEnd(e); }}
                    onMouseLeave={(e) => { isPanning.current = false; onDragEnd(e); }}
                    onClick={() => setSelectedNode(null)}
                    data-testid="swarm-canvas"
                  >
                    {/* Spacer div — sets scroll area to cover full visual content at any zoom */}
                    {nodes.length > 0 && (() => {
                      const maxX = Math.max(...nodes.map(n => n.x));
                      const maxY = Math.max(...nodes.map(n => n.y));
                      const contentW = (maxX + 400) * Math.max(zoom, 1) + 400;
                      const contentH = (maxY + 200) * Math.max(zoom, 1) + 200;
                      return <div style={{ position: "absolute", top: 0, left: 0, width: contentW, height: contentH, pointerEvents: "none" }} />;
                    })()}
                    <div style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, transformOrigin: "0 0", transition: isPanning.current ? "none" : "transform 0.15s ease-out" }}>
                      <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, width: nodes.length > 0 ? `${Math.max(...nodes.map(n => n.x)) + 800}px` : "100%", height: nodes.length > 0 ? `${Math.max(...nodes.map(n => n.y)) + 250}px` : "100%" }}>
                        {edges.map((edge, i) => (
                          <CanvasEdge
                            key={i}
                            from={nodeMap[edge.from]}
                            to={nodeMap[edge.to]}
                            executing={executing}
                            completedEdges={executionProgress?.completedEdges}
                          />
                        ))}
                      </svg>
                      <div className="relative" style={{ zIndex: 2, minWidth: nodes.length > 0 ? `${Math.max(...nodes.map(n => n.x)) + 800}px` : "auto", minHeight: nodes.length > 0 ? `${Math.max(...nodes.map(n => n.y)) + 250}px` : "auto" }}>
                      {nodes.map((node) => (
                        <CanvasNode
                          key={node.id}
                          node={node}
                          selected={selectedNode === node.id}
                          onSelect={setSelectedNode}
                          onDragStart={onDragStart}
                          executing={executing}
                          executionProgress={executionProgress}
                          onViewOutput={setViewingOutput}
                          agentResult={agentResultsMap[node.id]}
                        />
                      ))}
                    </div>
                    </div>{/* close transform wrapper */}
                    {nodes.length === 0 && !executing && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--qn-foreground-muted)]">
                        <GitBranch className="w-12 h-12 mb-4 opacity-20" />
                        <p className="text-sm">Add agents from the toolbar above or load a template</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => setTemplateDialogOpen(true)}>
                          <FolderOpen className="w-4 h-4 mr-2" /> Browse Templates
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Execution Log */}
              {executionProgress && executionProgress.log.length > 0 && (
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="execution-log">
                  <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                    <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">
                      <Terminal className="w-3.5 h-3.5 inline mr-2" />Execution Log
                    </CardTitle>
                    {executing && <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />}
                  </CardHeader>
                  <CardContent className="p-2 max-h-[160px] overflow-y-auto font-mono text-xs">
                    {executionProgress.log.map((entry, i) => (
                      <div key={i} className="flex items-start gap-2 py-0.5 px-2">
                        <span className="text-[var(--qn-foreground-subtle)] shrink-0 w-[70px]">{entry.time}</span>
                        <span className={
                          entry.type === "success" ? "text-emerald-500" :
                          entry.type === "error" ? "text-red-500" :
                          entry.type === "warning" ? "text-amber-500" :
                          "text-[var(--qn-foreground-muted)]"
                        }>
                          {entry.msg}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Agent Outputs Panel — Universal Results Viewer for Output Pipeline, else HAVEN/HARVEST */}
              {(() => {
                const liveDetail = {
                  swarm_name: swarmName,
                  execution_id: executionSummary?.execution_id || "live",
                  status: executionSummary?.status || "completed",
                  duration_ms: executionSummary?.duration_ms,
                  total_tokens: executionSummary?.total_tokens,
                  total_cost: executionSummary?.total_cost,
                  agent_results: Object.fromEntries(agentResults.map(r => [r.node_id, r])),
                };
                if (agentResults.length > 0 && hasOutputPipeline(liveDetail) && (executionSummary || !executing)) {
                  return <UniversalResultsViewer executionDetail={liveDetail} />;
                }
                if (agentResults.length > 0 && swarmName.toLowerCase().includes("harvest") && (executionSummary || !executing)) {
                  return <HarvestExecutiveDashboard executionDetail={liveDetail} />;
                }
                if (agentResults.length > 0 && swarmName.toLowerCase().includes("haven") && (executionSummary || !executing)) {
                  return <HavenExecutiveDashboard executionDetail={liveDetail} />;
                }
                return null;
              })()}

              {/* Full Results Panel - Shows all agent outputs organized */}
              {showResultsPanel && agentResults.length > 0 && (
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="full-results-panel">
                  <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                    <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)]">
                      <Brain className="w-3.5 h-3.5 inline mr-2" />All Agent Outputs ({agentResults.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {executionSummary && (
                        <span className="text-[10px] text-[var(--qn-foreground-muted)]">
                          {executionSummary.total_tokens?.toLocaleString()} tokens &middot; {fmtCost(executionSummary.total_cost)}
                        </span>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setShowResultsPanel(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 space-y-2 max-h-[600px] overflow-y-auto">
                    {agentResults.map((result) => (
                      <NodeOutputCard
                        key={result.node_id}
                        result={result}
                        expanded={!!expandedOutputs[result.node_id]}
                        onToggle={() => toggleOutput(result.node_id)}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          {/* Execution Detail View */}
          {selectedExecution && executionDetail ? (
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="execution-detail-view">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedExecution(null); setExecutionDetail(null); }} className="mb-2 text-xs text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="back-to-history-btn">
                    <ArrowRight className="w-3 h-3 mr-1 rotate-180" /> Back to History
                  </Button>
                  <CardTitle className="text-base font-outfit">{executionDetail.swarm_name || executionDetail.template_name || "Execution"}</CardTitle>
                  <p className="text-xs text-[var(--qn-foreground-muted)] mt-1">
                    {executionDetail.execution_id} &middot; {executionDetail.status} &middot; {executionDetail.duration_ms ? `${(executionDetail.duration_ms / 1000).toFixed(1)}s` : "—"} &middot; {(executionDetail.total_tokens || 0).toLocaleString()} tokens &middot; {fmtCost(executionDetail.total_cost)}
                  </p>
                </div>
                <Badge variant={executionDetail.status === "completed" ? "default" : "destructive"} className="text-xs">{executionDetail.status}</Badge>
              </CardHeader>
              <CardContent>
                {/* Universal Results Viewer, HARVEST, or HAVEN Executive Dashboard */}
                {hasOutputPipeline(executionDetail) ? (
                  <UniversalResultsViewer executionDetail={executionDetail} />
                ) : isHarvestExecution(executionDetail) ? (
                  <HarvestExecutiveDashboard executionDetail={executionDetail} />
                ) : isHavenExecution(executionDetail) ? (
                  <HavenExecutiveDashboard executionDetail={executionDetail} />
                ) : (
                <>
                {/* Prompt */}
                {executionDetail.prompt && (
                  <div className="mb-4 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]/20" data-testid="execution-prompt">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-1.5">
                      <Send className="w-3 h-3 inline mr-1" /> Prompt
                    </div>
                    <p className="text-sm text-[var(--qn-foreground)]">{executionDetail.prompt}</p>
                  </div>
                )}

                {/* Agent Outputs */}
                {executionDetailLoading ? (
                  <div className="flex items-center justify-center py-12 text-[var(--qn-foreground-muted)]">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading outputs...
                  </div>
                ) : executionDetail.agent_results && Object.keys(executionDetail.agent_results).length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--qn-foreground-muted)] mb-2">
                      <Brain className="w-3 h-3 inline mr-1" /> Agent Outputs ({Object.keys(executionDetail.agent_results).length})
                    </div>
                    {/* Sort by node_id to maintain DAG order */}
                    {Object.values(executionDetail.agent_results)
                      .sort((a, b) => (a.completed_at || "").localeCompare(b.completed_at || ""))
                      .map((result) => (
                        <NodeOutputCard
                          key={result.node_id}
                          result={result}
                          expanded={!!detailExpandedOutputs[result.node_id]}
                          onToggle={() => setDetailExpandedOutputs(prev => ({ ...prev, [result.node_id]: !prev[result.node_id] }))}
                        />
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--qn-foreground-muted)] py-8 text-center">No agent outputs available for this execution.</p>
                )}
                </>
                )}
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="swarm-history">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-outfit">Execution History</CardTitle>
              <Button variant="outline" size="sm" onClick={loadHistory} data-testid="refresh-history-btn">
                <Activity className="w-3.5 h-3.5 mr-2" /> Refresh
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12 text-[var(--qn-foreground-muted)]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history...
                </div>
              ) : executionHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-[var(--qn-foreground-muted)]">
                  <History className="w-10 h-10 mb-3 opacity-20" />
                  <p className="text-sm">No executions yet. Run a swarm to see results here.</p>
                </div>
              ) : (
                <>
                  <p className="px-4 py-2 text-xs text-[var(--qn-foreground-muted)] border-b border-[var(--qn-border)]/30">Click any execution to view full agent outputs</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--qn-border)] bg-[var(--qn-surface)]/50">
                        <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Execution ID</th>
                        <th className="text-left py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Swarm</th>
                        <th className="text-center py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Status</th>
                        <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Duration</th>
                        <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Agents</th>
                        <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Tokens</th>
                        <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Cost</th>
                        <th className="text-right py-3 px-4 text-[var(--qn-foreground-muted)] font-medium text-xs">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {executionHistory.map((exec, idx) => (
                        <motion.tr
                          key={exec.execution_id || idx}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.03 }}
                          className="border-b border-[var(--qn-border)]/30 hover:bg-[var(--qn-surface)]/30 transition-colors cursor-pointer"
                          onClick={() => loadExecutionDetail(exec.execution_id)}
                          data-testid={`history-row-${exec.execution_id}`}
                        >
                          <td className="py-3 px-4 font-mono text-xs text-[var(--qn-primary)] underline">{exec.execution_id}</td>
                          <td className="py-3 px-4 text-xs text-[var(--qn-foreground)]">{exec.template_name || exec.swarm_name || "—"}</td>
                          <td className="py-3 px-4 text-center">
                            <Badge
                              variant={exec.status === "completed" ? "default" : exec.status === "failed" ? "destructive" : "outline"}
                              className="text-[10px]"
                            >
                              {exec.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right text-xs font-mono text-[var(--qn-foreground)]">
                            {exec.duration_ms ? `${(exec.duration_ms / 1000).toFixed(1)}s` : "—"}
                          </td>
                          <td className="py-3 px-4 text-right text-xs font-mono text-[var(--qn-foreground)]">{exec.agents || "—"}</td>
                          <td className="py-3 px-4 text-right text-xs font-mono text-[var(--qn-foreground)]">
                            {exec.total_tokens ? exec.total_tokens.toLocaleString() : "—"}
                          </td>
                          <td className="py-3 px-4 text-right text-xs font-mono text-emerald-500">
                            {exec.total_cost != null ? fmtCost(exec.total_cost) : "—"}
                          </td>
                          <td className="py-3 px-4 text-right text-[11px] text-[var(--qn-foreground-muted)]">
                            {exec.created_at ? new Date(exec.created_at).toLocaleString() : "—"}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-4 gap-4 mb-6" data-testid="swarm-analytics">
            {[
              { label: "Total Executions", value: analyticsData?.total_executions ?? 0, icon: Play, color: "var(--qn-primary)" },
              { label: "Success Rate", value: `${analyticsData?.success_rate ?? 0}%`, icon: CheckCircle, color: "#10b981" },
              { label: "Total Tokens", value: (analyticsData?.total_tokens ?? 0).toLocaleString(), icon: Zap, color: "#f59e0b" },
              { label: "Total Cost", value: `$${(analyticsData?.total_cost ?? 0).toFixed(4)}`, icon: Activity, color: "#8b5cf6" },
            ].map((card) => (
              <Card key={card.label} className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: card.color + "15" }}>
                    <card.icon className="w-5 h-5" style={{ color: card.color }} />
                  </div>
                  <div>
                    <div className="text-xs text-[var(--qn-foreground-muted)]">{card.label}</div>
                    <div className="text-xl font-bold font-outfit text-[var(--qn-foreground)]">{card.value}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Agent Usage */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-outfit">Agent Role Usage</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsData?.agent_usage && Object.keys(analyticsData.agent_usage).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analyticsData.agent_usage)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 8)
                      .map(([role, count]) => {
                        const maxCount = Math.max(...Object.values(analyticsData.agent_usage));
                        const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                        return (
                          <div key={role} className="flex items-center gap-3">
                            <span className="text-xs font-medium text-[var(--qn-foreground)] w-36 truncate">{role}</span>
                            <div className="flex-1 h-2 rounded-full bg-[var(--qn-surface)] overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--qn-primary)]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-mono text-[var(--qn-foreground-muted)] w-10 text-right">{count}</span>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--qn-foreground-muted)] py-4">No agent usage data yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Template Popularity */}
            <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-outfit">Template Popularity</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsData?.template_popularity && Object.keys(analyticsData.template_popularity).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analyticsData.template_popularity)
                      .sort(([,a], [,b]) => b - a)
                      .map(([name, count], i) => (
                        <div key={name} className="flex items-center justify-between py-1.5 border-b border-[var(--qn-border)]/30">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-[var(--qn-foreground-muted)] w-5">#{i + 1}</span>
                            <span className="text-xs font-medium text-[var(--qn-foreground)]">{name}</span>
                          </div>
                          <span className="text-xs font-mono text-[var(--qn-foreground-muted)]">{count} runs</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--qn-foreground-muted)] py-4">No template data yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Agent Output Viewer Modal */}
      <Dialog open={!!viewingOutput} onOpenChange={() => setViewingOutput(null)}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-3xl max-h-[85vh]" data-testid="agent-output-modal">
          {viewingOutput && (() => {
            const at = AGENT_TYPES.find(a => a.type === viewingOutput.type) || AGENT_TYPES[0];
            const AtIcon = at.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: at.color + "20" }}>
                      <AtIcon className="w-4 h-4" style={{ color: at.color }} />
                    </div>
                    <div>
                      <div className="text-base font-semibold">{viewingOutput.label}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)] font-normal">
                        {at.label} &middot; {viewingOutput.tokens} tokens &middot; ${viewingOutput.cost?.toFixed(4)} &middot; {viewingOutput.latency_ms}ms
                      </div>
                    </div>
                    {viewingOutput.error ? (
                      <Badge variant="destructive" className="ml-auto">Error</Badge>
                    ) : (
                      <Badge className="ml-auto bg-emerald-600">Complete</Badge>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="overflow-y-auto max-h-[65vh] pr-2">
                  {viewingOutput.model_display_name && (
                    <div className="mb-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-[10px]">
                      <div className="flex items-center gap-3 text-amber-400">
                        <span className="font-semibold">Model: {viewingOutput.model_display_name}</span>
                        <span>Provider: {viewingOutput.model_provider || "—"}</span>
                      </div>
                    </div>
                  )}
                  {viewingOutput.media_type === "image" && viewingOutput.media_url && (
                    <div className="mb-3 rounded-lg overflow-hidden border border-[var(--qn-border)]">
                      <img src={`${API}${viewingOutput.media_url}`} alt={viewingOutput.label} className="w-full max-h-[400px] object-contain bg-black/20" />
                    </div>
                  )}
                  {viewingOutput.media_type === "video" && viewingOutput.media_url && (
                    <div className="mb-3 rounded-lg overflow-hidden border border-[var(--qn-border)]">
                      <video src={`${API}${viewingOutput.media_url}`} controls className="w-full max-h-[400px]" />
                    </div>
                  )}
                  <div className="p-4 rounded-lg bg-[var(--qn-surface)]/50 border border-[var(--qn-border)]">
                    <div className="text-sm text-[var(--qn-foreground)] leading-relaxed prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown>{viewingOutput.output}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Swarm Templates</DialogTitle>
            <p className="text-xs text-[var(--qn-foreground-muted)]">Pre-built agent workflows ready to execute</p>
          </DialogHeader>
          <div className="max-h-[500px] overflow-y-auto py-2 space-y-4">
            {/* Standard Templates */}
            <div>
              <h3 className="text-xs font-semibold text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-2 px-1">Standard & Enterprise</h3>
              <div className="grid grid-cols-2 gap-3">
                {TEMPLATES_WITH_VISTA.filter(t => t.category !== "haven" && t.category !== "harvest").map((tmpl) => (
                  <button
                    key={tmpl.id}
                    onClick={() => loadTemplate(tmpl)}
                    className="text-left p-4 rounded-xl border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40 bg-[var(--qn-surface)]/30 transition-all"
                    data-testid={`template-${tmpl.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-4 h-4 text-[var(--qn-primary)]" />
                      <span className="text-sm font-medium text-[var(--qn-foreground)]">{tmpl.name}</span>
                    </div>
                    <p className="text-xs text-[var(--qn-foreground-muted)] mb-2 line-clamp-2">{tmpl.desc}</p>
                    <div className="flex items-center gap-3 text-[10px] text-[var(--qn-foreground-subtle)]">
                      <span>{tmpl.nodes.length} agents</span>
                      <span>{tmpl.edges.length} connections</span>
                      <Badge variant="outline" className="text-[9px]">{tmpl.category}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* HAVEN Templates */}
            <div>
              <div className="flex items-center gap-2 mb-2 px-1">
                <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Project HAVEN</h3>
                <Badge className="text-[9px] bg-emerald-600/20 text-emerald-400 border-emerald-500/30">Affordable Housing</Badge>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {/* Master template first */}
                {TEMPLATES_WITH_VISTA.filter(t => t.id === "haven_master").map((tmpl) => (
                  <button
                    key={tmpl.id}
                    onClick={() => loadTemplate(tmpl)}
                    className="text-left p-4 rounded-xl border-2 border-emerald-500/30 hover:border-emerald-400/60 bg-emerald-500/5 transition-all"
                    data-testid={`template-${tmpl.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-400">{tmpl.name}</span>
                    </div>
                    <p className="text-xs text-[var(--qn-foreground-muted)] mb-2">{tmpl.desc}</p>
                    <div className="flex items-center gap-3 text-[10px] text-emerald-400/80">
                      <span>{tmpl.nodes.length} agents</span>
                      <span>{tmpl.edges.length} connections</span>
                      <span>10 divisions</span>
                    </div>
                  </button>
                ))}
                {/* Division templates */}
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATES_WITH_VISTA.filter(t => t.category === "haven" && t.id !== "haven_master").map((tmpl) => (
                    <button
                      key={tmpl.id}
                      onClick={() => loadTemplate(tmpl)}
                      className="text-left p-3 rounded-xl border border-emerald-500/20 hover:border-emerald-400/40 bg-[var(--qn-surface)]/30 transition-all"
                      data-testid={`template-${tmpl.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Layers className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-xs font-medium text-[var(--qn-foreground)]">{tmpl.name.replace("HAVEN: ", "")}</span>
                      </div>
                      <p className="text-[10px] text-[var(--qn-foreground-muted)] mb-1.5 line-clamp-2">{tmpl.desc}</p>
                      <div className="flex items-center gap-2 text-[9px] text-[var(--qn-foreground-subtle)]">
                        <span>{tmpl.nodes.length} agents</span>
                        <span>{tmpl.edges.length} connections</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* HARVEST Templates */}
            <div>
              <div className="flex items-center gap-2 mb-2 px-1">
                <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Project HARVEST</h3>
                <Badge className="text-[9px] bg-amber-600/20 text-amber-400 border-amber-500/30">Food Supply Chain</Badge>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {/* Master template first */}
                {TEMPLATES_WITH_VISTA.filter(t => t.id === "harvest-master").map((tmpl) => (
                  <button
                    key={tmpl.id}
                    onClick={() => navigate("/harvest/live")}
                    className="text-left p-4 rounded-xl border-2 border-amber-500/30 hover:border-amber-400/60 bg-amber-500/5 transition-all"
                    data-testid={`template-${tmpl.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-semibold text-amber-400">{tmpl.name}</span>
                      <Badge className="text-[9px] bg-emerald-600/20 text-emerald-300 border-emerald-500/30 ml-auto">Live Workspace</Badge>
                    </div>
                    <p className="text-xs text-[var(--qn-foreground-muted)] mb-2">{tmpl.desc}</p>
                    <div className="flex items-center gap-3 text-[10px] text-amber-400/80">
                      <span>{tmpl.nodes.length} illustrative agents</span>
                      <span>{tmpl.edges.length} connections</span>
                      <span>10 divisions</span>
                    </div>
                  </button>
                ))}
                {/* Division templates */}
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATES_WITH_VISTA.filter(t => t.category === "harvest" && t.id !== "harvest-master").map((tmpl) => (
                    <button
                      key={tmpl.id}
                      onClick={() => loadTemplate(tmpl)}
                      className="text-left p-3 rounded-xl border border-amber-500/20 hover:border-amber-400/40 bg-[var(--qn-surface)]/30 transition-all"
                      data-testid={`template-${tmpl.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Layers className="w-3.5 h-3.5 text-amber-400" />
                        <span className="text-xs font-medium text-[var(--qn-foreground)]">{tmpl.name.replace("HARVEST: ", "")}</span>
                      </div>
                      <p className="text-[10px] text-[var(--qn-foreground-muted)] mb-1.5 line-clamp-2">{tmpl.desc}</p>
                      <div className="flex items-center gap-2 text-[9px] text-[var(--qn-foreground-subtle)]">
                        <span>{tmpl.nodes.length} agents</span>
                        <span>{tmpl.edges.length} connections</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
